import React from 'react';
import { useResponsive } from '../hooks/useResponsive';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import NavbarAuth from '../components/NavbarAuth';

// ═══════════════════════════════════════════════════════════════════════
//  PAGE LÉGALE — Zawaj & Tawadud
//  Regroupe : Politique de confidentialité · CGV
//
//  ⚠️  La section MENTIONS LÉGALES a été volontairement retirée : elle exige
//      l'identité légale de l'éditeur (nom/raison sociale, adresse, SIRET),
//      que seul le propriétaire peut fournir. À ajouter dès qu'elle est connue.
//
//  ⚠️  Si un jour l'upload de VRAIES photos par les membres est activé
//      (aujourd'hui : uniquement des avatars prédéfinis), il faudra
//      ajouter Cloudinary comme sous-traitant + mention transfert hors UE
//      dans la section « Confidentialité ».
//
//  Ce texte est un point de départ sérieux mais n'a pas valeur d'avis
//  juridique. Pour un site commercial traitant des données sensibles,
//  une relecture par un professionnel est recommandée.
// ═══════════════════════════════════════════════════════════════════════

const Legal = () => {
    const { user } = useAuth();
    const { isSmall, isMobile } = useResponsive();

    const h2 = {
        color: '#1a0010',
        fontSize: isSmall ? '22px' : isMobile ? '26px' : '30px',
        fontWeight: '700',
        fontFamily: 'Cormorant Garamond, serif',
        margin: '0 0 18px',
        paddingBottom: '10px',
        borderBottom: '2px solid #f0d9e5',
    };
    const h3 = {
        color: '#781840',
        fontSize: isSmall ? '15px' : '17px',
        fontWeight: '700',
        margin: '24px 0 8px',
    };
    const p = {
        color: '#4a3540',
        fontSize: isSmall ? '13px' : '14px',
        lineHeight: '1.8',
        margin: '0 0 12px',
    };
    const li = { ...p, margin: '0 0 6px' };
    const placeholder = {
        display: 'inline-block',
        background: '#fdeff5',
        color: '#c0306a',
        fontWeight: '700',
        padding: '1px 8px',
        borderRadius: '5px',
        fontSize: '13px',
    };
    const block = {
        background: '#fff',
        borderRadius: '18px',
        padding: isSmall ? '24px 20px' : isMobile ? '32px 26px' : '44px 48px',
        boxShadow: '0 1px 6px rgba(120,24,64,0.08)',
        marginBottom: '28px',
    };

    return (
        <div style={{ fontFamily: 'Arial, sans-serif', overflowX: 'hidden', backgroundColor: '#faf6f8' }}>

            {/* ═══════════ NAVBAR ═══════════ */}
            {user ? <NavbarAuth /> : <Navbar />}

            {/* ═══════════ HERO ═══════════ */}
            <section style={{ position: 'relative', backgroundColor: '#1a0010', overflow: 'hidden' }}>
                <div style={{
                    position: 'relative', zIndex: 2,
                    maxWidth: '1200px', margin: '0 auto',
                    padding: isSmall ? '40px 20px' : isMobile ? '48px 24px' : '58px 48px',
                }}>
                    <p style={{ color: '#E88BB4', fontSize: '11px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '12px', marginTop: 0 }}>
                        CONFIDENTIALITÉ & CGV
                    </p>
                    <h1 style={{
                        color: 'white', fontWeight: '700',
                        fontSize: isSmall ? '30px' : isMobile ? '36px' : '46px',
                        lineHeight: '1.15', margin: 0,
                        fontFamily: 'Cormorant Garamond, serif',
                    }}>
                        Confidentialité et conditions d'utilisation
                    </h1>
                </div>
            </section>

            {/* ═══════════ CONTENU ═══════════ */}
            <section style={{ padding: isSmall ? '32px 16px' : isMobile ? '40px 24px' : '56px 48px' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>

                    <p style={{ ...p, textAlign: 'center', color: '#8a7580', marginBottom: '32px' }}>
                        Dernière mise à jour : 15 août 2026
                    </p>

                    {/* ─────────── 2. CONFIDENTIALITÉ ─────────── */}
                    <div style={block}>
                        <h2 style={h2}>1. Politique de confidentialité</h2>
                        <p style={p}>
                            Zawaj &amp; Tawadud accorde une grande importance à la protection de vos données personnelles,
                            conformément au Règlement général sur la protection des données (RGPD).
                        </p>

                        <h3 style={h3}>Données collectées</h3>
                        <p style={p}>Dans le cadre de la création et de l'utilisation de votre compte, nous collectons :</p>
                        <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                            <li style={li}>vos identifiants de connexion (adresse email et mot de passe, ce dernier étant stocké de façon chiffrée) ;</li>
                            <li style={li}>les informations de votre profil : prénom, date de naissance, âge, genre, ville, pays d'origine et de résidence, langues, situation matrimoniale, profession, niveau d'études, ethnie, taille, poids, présentation ;</li>
                            <li style={li}>des données relatives à vos convictions religieuses (pratique religieuse, prière, port du voile ou de la barbe, école de pensée, situation de converti). Ces données, dites « sensibles » au sens du RGPD, sont traitées avec votre consentement explicite et dans le seul but de la mise en relation matrimoniale ;</li>
                            <li style={li}>les coordonnées du tuteur (wali) que vous choisissez de renseigner ;</li>
                            <li style={li}>les données liées à votre abonnement et à vos paiements.</li>
                        </ul>
                        <p style={p}>
                            Les photos de profil utilisées sur la plateforme sont des <strong>avatars prédéfinis</strong> fournis
                            par le site : elles ne constituent pas des données personnelles vous concernant.
                        </p>

                        <h3 style={h3}>Finalité du traitement</h3>
                        <p style={p}>
                            Vos données sont utilisées exclusivement pour permettre la mise en relation matrimoniale entre membres,
                            dans un cadre sérieux et respectueux des valeurs de la plateforme. Elles ne sont jamais vendues ni cédées à des tiers à des fins commerciales.
                        </p>

                        <h3 style={h3}>Prestataires (sous-traitants)</h3>
                        <p style={p}>
                            Pour fonctionner, le site fait appel à des prestataires qui peuvent traiter certaines de vos données pour notre compte :
                        </p>
                        <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                            <li style={li}><strong>Stripe</strong> — traitement sécurisé des paiements d'abonnement. Vos données bancaires sont transmises directement à Stripe et ne sont jamais stockées sur nos serveurs ;</li>
                            <li style={li}><strong>Render</strong> — hébergement du site et de la base de données (infrastructure en Union européenne, Francfort) ;</li>
                            <li style={li}><strong>OVH</strong> — gestion du nom de domaine.</li>
                        </ul>

                        <h3 style={h3}>Durée de conservation</h3>
                        <p style={p}>
                            Vos données sont conservées tant que votre compte est actif. En cas de suppression de votre compte,
                            elles sont effacées ou anonymisées dans un délai raisonnable, sous réserve des obligations légales
                            de conservation (notamment comptables pour les données de paiement).
                        </p>

                        <h3 style={h3}>Vos droits</h3>
                        <p style={p}>
                            Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression, de limitation
                            et de portabilité de vos données, ainsi que du droit de retirer votre consentement à tout moment.
                            Pour exercer ces droits, adressez votre demande via la page{' '}
                            <a href="/contact" style={{ color: '#c0306a', fontWeight: 700 }}>Contact</a>, accessible même sans être connecté.
                            Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
                        </p>
                    </div>

                    {/* ─────────── 3. CGV / CGU ─────────── */}
                    <div style={block}>
                        <h2 style={h2}>2. Conditions générales d'utilisation et de vente</h2>

                        <h3 style={h3}>Objet du service</h3>
                        <p style={p}>
                            Zawaj &amp; Tawadud est une plateforme de mise en relation matrimoniale à destination de personnes
                            majeures souhaitant se marier dans le respect des valeurs de l'Islam. L'inscription est réservée aux personnes âgées d'au moins 18 ans.
                        </p>

                        <h3 style={h3}>Fonctionnement</h3>
                        <p style={p}>
                            Après inscription et validation du profil, l'accès complet aux profils et aux demandes de mise en relation
                            nécessite la souscription d'un abonnement. Les échanges directs ne sont pas possibles : les mises en relation
                            sont encadrées par la plateforme afin de préserver un cadre sérieux.
                        </p>

                        <h3 style={h3}>Abonnements et tarifs</h3>
                        <p style={p}>
                            L'accès aux fonctionnalités payantes se fait via l'une des formules proposées :
                        </p>
                        <ul style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
                            <li style={li}>1 mois d'accès : 1,90 €</li>
                            <li style={li}>3 mois d'accès : 3,90 €</li>
                            <li style={li}>6 mois d'accès : 6,90 €</li>
                        </ul>
                        <p style={p}>
                            Les formules sont <strong>sans engagement</strong> et à durée déterminée : elles ne se renouvellent pas
                            automatiquement. À l'issue de la période choisie, l'accès aux fonctionnalités est suspendu jusqu'au renouvellement.
                            Les tarifs indiqués sont susceptibles d'évoluer ; le tarif applicable est celui affiché au moment de la souscription.
                        </p>

                        <h3 style={h3}>Paiement</h3>
                        <p style={p}>
                            Les paiements sont traités de manière sécurisée par Stripe. Aucune donnée bancaire n'est conservée par la plateforme.
                        </p>

                        <h3 style={h3}>Droit de rétractation</h3>
                        <p style={p}>
                            L'abonnement donne accès immédiatement au service dès la validation du paiement. Conformément à
                            l'article L221-28 du Code de la consommation, en souscrivant un abonnement, l'utilisateur demande
                            expressément l'accès immédiat au service et <strong>reconnaît renoncer à son droit de rétractation
                                de 14 jours</strong>. Aucun remboursement ne pourra donc être exigé une fois l'abonnement activé.
                        </p>

                        <h3 style={h3}>Responsabilités et bonne conduite</h3>
                        <p style={p}>
                            Chaque membre s'engage à fournir des informations exactes et à adopter un comportement respectueux.
                            La plateforme se réserve le droit de suspendre ou supprimer tout compte ne respectant pas ces conditions
                            ou les valeurs du site, sans que cela n'ouvre droit à remboursement. Zawaj &amp; Tawadud met en œuvre les
                            moyens nécessaires au bon fonctionnement du service mais ne peut garantir l'aboutissement d'une mise en relation.
                        </p>

                        <h3 style={h3}>Modification des conditions</h3>
                        <p style={p}>
                            Les présentes conditions peuvent être modifiées à tout moment. La version applicable est celle en vigueur
                            au moment de l'utilisation du service.
                        </p>
                    </div>

                    <p style={{ ...p, textAlign: 'center', color: '#8a7580', fontSize: '12px', marginTop: '8px' }}>
                        Pour toute question relative à ces informations, contactez-nous via la page{' '}
                        <a href="/contact" style={{ color: '#c0306a', fontWeight: 700 }}>Contact</a>.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Legal;